import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AddressOnlyForm, { AddressFormValues } from '../components/common/AddressOnlyForm';
import AddressValidationService from '../services/addressValidationService';
import {
  AddressValidationRequest,
  AddressValidationResponse,
} from '../types/address';
import { getCountryById, getStateById, getCityById } from '../services/locationService';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Kicker } from '../components/ui/Kicker';
import { Icon } from '../components/ui/Icon';

const AddressValidationPage: React.FC = () => {
  const { user } = useAuth();
  const [validationCount, setValidationCount] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<AddressValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    document.title = 'Address Validation | Almaship';
  }, []);

  const handleSubmit = async (data: AddressFormValues) => {
    setIsValidating(true);
    setError(null);
    setResult(null);

    try {
      let countryName = '';
      let stateName = '';
      let cityName = '';

      if (data.country_id) {
        try {
          const country = await getCountryById(data.country_id);
          countryName = country?.iso2 || '';
        } catch {
          /* swallow */
        }
      }
      if (data.state_id && data.country_id) {
        try {
          const state = await getStateById(data.country_id, data.state_id);
          stateName = state?.state_code || '';
        } catch {
          /* swallow */
        }
      }
      if (data.city_id && data.state_id) {
        try {
          const city = await getCityById(data.state_id, data.city_id);
          cityName = city?.name || '';
        } catch {
          /* swallow */
        }
      } else if (data.city_other) {
        cityName = data.city_other.trim();
      }

      const req: AddressValidationRequest = {
        street1: data.street,
        street2: data.houseNo || undefined,
        city: cityName,
        state: stateName || undefined,
        postalCode: data.zipCode,
        country: countryName,
      };

      const validationResult = await AddressValidationService.validateAddress(req);
      setValidationCount((c) => c + 1);
      setResult(validationResult);
      if (!validationResult.valid) {
        setError(
          validationResult.message ||
            'Address validation failed. Please check the details and try again.'
        );
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'An unexpected error occurred during validation.';
      setError(msg);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <Kicker>Tools</Kicker>
          <PageTitle style={{ marginTop: 8 }}>Address validation</PageTitle>
          <PageSub>Clean, verify, and standardize addresses before you ship.</PageSub>
          {user && (
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              Welcome, {user.first_name || user.email}! You have validated {validationCount}{' '}
              address{validationCount !== 1 ? 'es' : ''} this session.
            </div>
          )}
        </div>
      </PageHead>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="details-grid">
        <Card pad style={{ padding: 24 }}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>Validate an address</h3>
          {error && (
            <div style={{ marginBottom: 14 }}>
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <span>{error}</span>
              </Callout>
            </div>
          )}
          <AddressOnlyForm
            onSubmit={handleSubmit}
            isSubmitting={isValidating}
            formError={error}
          />
        </Card>

        <Card pad style={{ padding: 24 }}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}
          >
            <h3 style={{ fontSize: 15 }}>Result</h3>
            {result?.valid && <Badge tone="ok" dot>Deliverable</Badge>}
            {result && !result.valid && <Badge tone="err" dot>Issues found</Badge>}
            {!result && !isValidating && <Badge>Awaiting input</Badge>}
            {isValidating && <Badge tone="info" dot>Validating…</Badge>}
          </div>

          {!result && !isValidating && (
            <div className="ph" style={{ height: 220, borderRadius: 10 }}>
              ENTER AN ADDRESS TO VALIDATE
            </div>
          )}

          {result?.normalized_address && (
            <div style={{ padding: 16, background: 'var(--bg-tint)', borderRadius: 10 }}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
                STANDARDIZED
              </div>
              <div style={{ marginTop: 4, fontWeight: 500, lineHeight: 1.5 }}>
                {result.normalized_address.street1}
                {result.normalized_address.street2 && (
                  <>, {result.normalized_address.street2}</>
                )}
                <br />
                {result.normalized_address.city}, {result.normalized_address.state}{' '}
                {result.normalized_address.postalCode}
                <br />
                {result.normalized_address.country}
              </div>
            </div>
          )}

          {result?.warnings && result.warnings.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <div>
                  <strong>Warnings:</strong>
                  <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                    {result.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              </Callout>
            </div>
          )}

          {result?.suggestions && result.suggestions.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <Callout tone="info">
                <Icon name="info" size={16} />
                <div>
                  <strong>Suggestions:</strong>
                  <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                    {result.suggestions.map((s, i) => (
                      <li key={i}>
                        {typeof s === 'string'
                          ? s
                          : `${s.street1 || ''} ${s.street2 || ''}, ${s.city || ''}, ${s.state || ''} ${s.postalCode || ''}`}
                      </li>
                    ))}
                  </ul>
                </div>
              </Callout>
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
};

export default AddressValidationPage;
